import { useState, useCallback, useRef, useEffect, forwardRef, useImperativeHandle, } from 'react'
import styles from './PdfViewer.module.scss'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { SpinLoading, Mask } from 'antd-mobile'

import { Document, Page, pdfjs } from 'react-pdf'
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "/worker/pdf.worker.js", // 从 public/worker 目录里读
  import.meta.url,
).toString();

// pdfjs.GlobalWorkerOptions.workerSrc = '/lib/pdfjs/pdf.worker.min.mjs'
type PdfViewerProps = {
  file: string
  className?: string
  onMounted?: (pagenNum: number) => void
}

export interface PdfViewerRefHandle {
  setShowLoading: (show: boolean) => void
}

interface CustomWindow extends Window {
  safari?: any
}

 const Loading = ({showLoading}: {showLoading: boolean}) => {
    return (
      <>
        <Mask visible={showLoading} opacity="thick">
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            color: 'white'
          }}>
            <SpinLoading style={{ '--size': '48px' }} />
            <p style={{ marginTop: 16 }}>加载中...</p>
          </div>
        </Mask>
      </>
    )
  }

const PdfViewer = forwardRef(({ file, className = '', onMounted }: PdfViewerProps, ref) => {
  const [numPages, setNumPages] = useState<number | null>(null)
  const [loadedPages, setLoadedPages] = useState(0)
  const scaleMin: number = 1
  const scaleMax: number = 3
  // 初始化要设置最大值，不然ios浏览器缩放后有布局异常
  const [scale, setScale] = useState<number>(scaleMax)
  const [showLoading, setShowLoading] = useState<boolean>(true)
  const initialDistance = useRef<number>(0)
  const initialScale = useRef<number>(1)
  const pdfContainer = useRef<HTMLDivElement>(null)
  const [pdfContainerWidth, setPdfContainerWidth] = useState<number>(0)
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    // 初始化设置成最小值
    setScale(scaleMin)
    // 文档加载成功页面还未加载,加延时
    // setTimeout(() => {
    //   setShowLoading(false)
    // }, 500);
    
    setNumPages(numPages)
    onMounted && onMounted(numPages)
  }

  const onPageLoadSuccess = () => {
    setLoadedPages(prev => {
      const newCount = prev + 1
      if (newCount === numPages) {
        setShowLoading(false)
      }
      return newCount
    })
  }

  // 处理文档加载失败
  const onDocumentLoadError = useCallback((error: Error) => {
    console.error(error)
  }, [])

  // 计算两点之间的距离
  const getDistance = (touches: any) => {
    const dx = touches[0].clientX - touches[1].clientX
    const dy = touches[0].clientY - touches[1].clientY
    return Math.sqrt(dx * dx + dy * dy)
  }
  // 判断是不是苹果浏览器
  function isSafari() {
    const userAgent = navigator.userAgent
    if (/^((?!Chrome|Chromium|CriOS).)*Safari/i.test(userAgent)) {
      return true
    }
    if ((window as CustomWindow).safari !== undefined) {
      return true
    }
    return false
  }

  useEffect(() => {
    setShowLoading(true)
    // 解决苹果浏览器放大后页面布局错乱，一开始就要按最大的比例显示
    setScale(scaleMax)
    setLoadedPages(0)
  }, [file])

  useEffect(() => {
    const viewer = pdfContainer.current
    if (!viewer) return

    const { width } = viewer.getBoundingClientRect()
    setPdfContainerWidth(width)
    const handleTouchStart = (e: TouchEvent) => {
      console.log('执行handleTouchStart')
      if (e.touches.length === 2) {
        e.preventDefault()
        initialDistance.current = getDistance(e.touches)
        initialScale.current = scale
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      console.log('执行handleTouchMove')
      if (e.touches.length === 2) {
        e.preventDefault()
        const currentDistance = getDistance(e.touches)
        const newScale = Math.max(scaleMin, Math.min(scaleMax,
          initialScale.current * (currentDistance / initialDistance.current)))
        setScale(newScale)
      }
    }
    viewer.addEventListener('touchstart', handleTouchStart, { passive: false })
    viewer.addEventListener('touchmove', handleTouchMove, { passive: false })
    return () => {
      viewer.removeEventListener('touchstart', handleTouchStart)
      viewer.removeEventListener('touchmove', handleTouchMove)
    }
  }, [])

  const onPageRenderError = (type: string, error?: Error) => {
    // 不涉及pdf渲染崩溃的错误
    const ignoreErrors = [
      'TextLayer task cancelled',
    ]
    if (error && error.message && ignoreErrors.some(msg => error.message.includes(msg))) {
      return
    }
    if (error) {
      throw new Error(`PDFViewer 渲染失败 ${type} : ${error.message}`)
    }
    throw new Error(`PDFViewer 渲染失败 ${type}`)
  }

  function render(nums: number) {
    return Array.from({ length: nums }, (_, i) => {
      const pageNumber = i + 1;
      return (
        <div key={pageNumber} className='pdfPageView'>
          <Page
            pageNumber={pageNumber}
            loading={<div></div>}
            className='pdfPage'
            onLoadSuccess={onPageLoadSuccess}
            onGetAnnotationsError={ () => onPageRenderError('onGetAnnotationsError')}
            onGetStructTreeError={ () => onPageRenderError('onGetStructTreeError')}
            onGetTextError={ (error) => onPageRenderError('onGetTextError', error)}
            onLoadError={ () => onPageRenderError('onLoadError')}
            onRenderAnnotationLayerError={ () => onPageRenderError('onRenderAnnotationLayerError')}
            onRenderError={ (error) => onPageRenderError('onRenderError', error)}
            onRenderTextLayerError={ (error) => onPageRenderError('onRenderTextLayerError', error)}
          />
        </div>
      );
    });
  }

  useImperativeHandle(ref, () => (
    { setShowLoading }
  ))
  return (<>
    {
      <div
        className={`${styles.pdfContainer} ${className}`}
        ref={pdfContainer}
      >
        <Loading showLoading={showLoading} />

        {/* 由于设置transform: `scale(${scale}) 苹果浏览器放大后字体不清晰并且无法显示滚动条所以改成widht形式 */}
        {/* <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}> */}
        <div style={{ width: `${scale * pdfContainerWidth}px` }}>
          {
            file && <Document
              className={styles.pdfDocument}
              file={file}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={<div className="loading"></div>}

            >
              {numPages && numPages > 0 && render(numPages)}
            </Document>
          }
        </div>
      </div>
    }
  </>)

})


export default PdfViewer

export const PdfCanvasViewer = forwardRef<PdfViewerRefHandle, PdfViewerProps>(({ file, onMounted }, ref) => {
  const [numPages, setNumPages] = useState<number | null>(null)
  const [loadedPages, setLoadedPages] = useState(0)
  const scaleMin: number = 1
  const scaleMax: number = 3
  const [scale, setScale] = useState<number>(scaleMin) // 改为从最小缩放开始
  const [showLoading, setShowLoading] = useState<boolean>(true)
  const initialDistance = useRef<number>(0)
  const initialScale = useRef<number>(1)
  const pdfContainer = useRef<HTMLDivElement>(null)
  const [pdfContainerWidth, setPdfContainerWidth] = useState<number>(0)
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([])
  const pdfDocRef = useRef<any>(null)
  const renderTasksRef = useRef<any[]>([])

  // 计算两点之间的距离
  const getDistance = (touches: any) => {
    const dx = touches[0].clientX - touches[1].clientX
    const dy = touches[0].clientY - touches[1].clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  // 渲染单个页面到 Canvas
  const renderPageToCanvas = useCallback(async (pageNumber: number, canvas: HTMLCanvasElement, renderScale: number) => {
    if (!pdfDocRef.current || !canvas) return

    try {
      const page = await pdfDocRef.current.getPage(pageNumber)
      
      // 取消之前的渲染任务
      if (renderTasksRef.current[pageNumber - 1]) {
        try {
          renderTasksRef.current[pageNumber - 1].cancel()
        } catch (e) {
          // 忽略取消错误
        }
      }

      // 计算合适的viewport
      // 获取页面原始尺寸
      const originalViewport = page.getViewport({ scale: 1 })
      
      // 计算适配容器宽度的缩放比例
      const containerWidth = pdfContainerWidth > 0 ? pdfContainerWidth : window.innerWidth - 32 // 减去padding
      const pageWidth = originalViewport.width
      const baseScale = containerWidth / pageWidth
      
      // 应用用户缩放
      const finalScale = baseScale * renderScale
      
      // 使用2倍分辨率渲染以提高清晰度
      const viewport = page.getViewport({ scale: finalScale * 2 })
      const context = canvas.getContext('2d', { alpha: false })
      
      if (!context) return

      // 设置 canvas 尺寸
      canvas.height = viewport.height
      canvas.width = viewport.width
      
      // 设置 CSS 尺寸（缩小到实际显示大小）
      canvas.style.width = `${viewport.width / 2}px`
      canvas.style.height = `${viewport.height / 2}px`

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      }

      // 开始渲染
      const renderTask = page.render(renderContext)
      renderTasksRef.current[pageNumber - 1] = renderTask

      await renderTask.promise
      
      // 更新加载进度
      setLoadedPages(prev => {
        const newCount = prev + 1
        if (newCount === numPages) {
          setShowLoading(false)
        }
        return newCount
      })

    } catch (error: any) {
      // 忽略渲染取消的错误
      if (error?.name === 'RenderingCancelledException') {
        return
      }
      console.error(`渲染第 ${pageNumber} 页失败:`, error)
    }
  }, [numPages, pdfContainerWidth])

  // 加载 PDF 文档
  const loadPDF = useCallback(async () => {
    if (!file) return

    try {
      console.log('开始加载 PDF...')
      
      const loadingTask = pdfjs.getDocument({
        url: file,
        standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
        cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
        cMapPacked: true,
      })
      
      const pdf = await loadingTask.promise
      pdfDocRef.current = pdf
      const pages = pdf.numPages
      
      console.log(`PDF 加载成功，共 ${pages} 页`)
      setNumPages(pages)
      onMounted && onMounted(pages)

    } catch (error) {
      console.error('❌ PDF 加载失败:', error)
      setShowLoading(false)
      throw new Error(`PDF 加载失败: ${error}`)
    }
  }, [file, onMounted])

  // 渲染所有页面
  const renderAllPages = useCallback(async () => {
    if (!pdfDocRef.current || !numPages || numPages === 0 || pdfContainerWidth === 0) {
      return
    }

    console.log(`开始渲染 ${numPages} 页，缩放: ${scale}, 容器宽度: ${pdfContainerWidth}`)
    
    // 重置加载进度
    setLoadedPages(0)

    // 依次渲染每一页
    for (let i = 1; i <= numPages; i++) {
      const canvas = canvasRefs.current[i - 1]
      if (canvas) {
        await renderPageToCanvas(i, canvas, scale)
      }
    }
  }, [numPages, scale, pdfContainerWidth, renderPageToCanvas])

  // 文件改变时重新加载
  useEffect(() => {
    setShowLoading(true)
    setScale(scaleMin) // 改为从最小缩放开始
    setLoadedPages(0)
    setNumPages(null)
    pdfDocRef.current = null
    renderTasksRef.current = []
    
    loadPDF()
  }, [file, loadPDF, scaleMin])

  // 页面数量确定后开始渲染
  useEffect(() => {
    if (numPages && numPages > 0 && pdfContainerWidth > 0) {
      // 延迟一帧，确保 canvas 元素已经创建
      requestAnimationFrame(() => {
        renderAllPages()
      })
    }
  }, [numPages, pdfContainerWidth, renderAllPages])

  // 缩放改变时重新渲染
  useEffect(() => {
    if (numPages && numPages > 0 && pdfDocRef.current && pdfContainerWidth > 0) {
      setShowLoading(true)
      renderAllPages()
    }
  }, [scale, numPages, pdfContainerWidth, renderAllPages])

  // 获取容器宽度并处理缩放手势
  useEffect(() => {
    const viewer = pdfContainer.current
    if (!viewer) return

    // 立即获取容器宽度
    const updateContainerWidth = () => {
      const { width } = viewer.getBoundingClientRect()
      setPdfContainerWidth(width)
    }
    
    updateContainerWidth()

    // 监听窗口大小变化
    window.addEventListener('resize', updateContainerWidth)

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault()
        initialDistance.current = getDistance(e.touches)
        initialScale.current = scale
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault()
        const currentDistance = getDistance(e.touches)
        const newScale = Math.max(
          scaleMin,
          Math.min(scaleMax, initialScale.current * (currentDistance / initialDistance.current))
        )
        setScale(newScale)
      }
    }

    viewer.addEventListener('touchstart', handleTouchStart, { passive: false })
    viewer.addEventListener('touchmove', handleTouchMove, { passive: false })

    return () => {
      window.removeEventListener('resize', updateContainerWidth)
      viewer.removeEventListener('touchstart', handleTouchStart)
      viewer.removeEventListener('touchmove', handleTouchMove)
    }
  }, [scale, scaleMin, scaleMax])

  // 清理渲染任务
  useEffect(() => {
    return () => {
      renderTasksRef.current.forEach(task => {
        if (task) {
          try {
            task.cancel()
          } catch (e) {
            // 忽略
          }
        }
      })
    }
  }, [])

  useImperativeHandle(ref, () => ({
    setShowLoading,
  }))

  return (
    <div className={styles.pdfContainer} ref={pdfContainer}>
       <Loading showLoading={showLoading} />
      
      <div style={{ width: '100%' }}>
        {numPages &&
          Array.from({ length: numPages }, (_, i) => (
            <div 
              key={i + 1} 
              className="pdfPageView" 
              style={{ 
                marginBottom: '10px',
                background: '#fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              <canvas
                ref={el => {
                  canvasRefs.current[i] = el
                }}
                style={{
                  display: 'block',
                  margin: '0 auto',
                  maxWidth: '100%',
                  height: 'auto',
                }}
              />
            </div>
          ))}
      </div>
    </div>
  )
})